import React, { useState, useEffect } from 'react';
import { Checkbox, Typography, Box, Divider, Chip, List, ListItem, ListItemText, ListItemIcon, Paper, Input, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Search, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useAuthHeader, useAuthUser } from '../Services/Auth/sessionAuth';
import axios from 'axios';
import { Crypter } from '../Functions/Security/Crypter';
import Data from '../Data/Data.json';
import Menus from '../Models/Menu';
import CusumMenuForSelect from '../Models/CusumMenuForSelect';

interface ModuleMappingBoxProps {
  selectedModules?: string[];
  onModulesChange?: (modules: string[]) => void;
  disabled?: boolean;
  isConsultMode?: boolean;
  title?: string;
}

interface ModuleNode {
  key: string;
  title: string;
  children?: ModuleNode[];
  disabled?: boolean;
}

const ModuleMappingBox: React.FC<ModuleMappingBoxProps> = ({
  selectedModules = [],
  onModulesChange,
  disabled = false,
  isConsultMode = false,
  title = "Modules du groupe"
}) => {
  const auth = useAuthUser();
  const authHeader = useAuthHeader();
  
  const [modules, setModules] = useState<ModuleNode[]>([]);
  const [nodeMap, setNodeMap] = useState<Record<string, ModuleNode>>({});
  const [parentMap, setParentMap] = useState<Record<string, string | null>>({});
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = Crypter.encrypt('all');
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.userMenus,
        headers: {
          'Authorization': authHeader(),
          'Content-Type': 'application/json',
          "CipherUserGroup": data
        }
      };

      const response = await axios.request(config);
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const menuData = CusumMenuForSelect.getMenu(res.Result);
          setModules(menuData);
          // build helper maps for parent/child lookups
          const nMap: Record<string, ModuleNode> = {};
          const pMap: Record<string, string | null> = {};
          const build = (nodes: ModuleNode[], parent: string | null = null) => {
            nodes.forEach(n => {
              nMap[n.key] = n;
              pMap[n.key] = parent;
              if (n.children) build(n.children, n.key);
            });
          };
          build(menuData, null);
          setNodeMap(nMap);
          setParentMap(pMap);
        } else {
          setError('Erreur lors du chargement des modules');
        }
      }
    } catch (err) {
      console.error('Error loading modules:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const handleModuleToggle = (moduleKey: string, checked: boolean) => {
    if (isConsultMode || disabled) return;

    const addParents = (key: string) => {
      const chain: string[] = [];
      let cur = parentMap[key];
      while (cur) {
        chain.push(cur);
        cur = parentMap[cur];
      }
      return chain;
    };

    const removeOrKeepParents = (selAfterRemoval: string[], key: string) => {
      // remove parent if none of its children remain selected
      let cur = parentMap[key];
      while (cur) {
        const parentNode = nodeMap[cur];
        const childKeys = parentNode ? (parentNode.children || []).map(c => c.key) : [];
        const anyChildSelected = childKeys.some(k => selAfterRemoval.includes(k));
        if (!anyChildSelected) {
          // remove parent
          selAfterRemoval = selAfterRemoval.filter(s => s !== cur);
          cur = parentMap[cur];
        } else {
          break;
        }
      }
      return selAfterRemoval;
    };

    if (checked) {
      const parents = addParents(moduleKey);
      const newSelectedModules = Array.from(new Set([...selectedModules, moduleKey, ...parents]));
      onModulesChange?.(newSelectedModules);
    } else {
      let newSelectedModules = selectedModules.filter(k => k !== moduleKey);
      newSelectedModules = removeOrKeepParents(newSelectedModules, moduleKey);
      onModulesChange?.(newSelectedModules);
    }
  };

  const handleParentToggle = (parentNode: ModuleNode, checked: boolean) => {
    if (isConsultMode || disabled) return;
    
    const getAllChildKeys = (node: ModuleNode): string[] => {
      const keys = [node.key];
      if (node.children) {
        node.children.forEach(child => {
          keys.push(...getAllChildKeys(child));
        });
      }
      return keys;
    };
    
    const childKeys = getAllChildKeys(parentNode);
    const newSelectedModules = checked
      ? Array.from(new Set([...selectedModules, ...childKeys]))
      : selectedModules.filter(key => !childKeys.includes(key));
    
    onModulesChange?.(newSelectedModules);
  };

  const toggleNodeExpansion = (nodeKey: string) => {
    setExpandedNodes(prev => 
      prev.includes(nodeKey) 
        ? prev.filter(key => key !== nodeKey)
        : [...prev, nodeKey]
    );
  };

  const filterModules = (nodes: ModuleNode[], term: string): ModuleNode[] => {
    if (!term) return nodes;
    
    return nodes.reduce((acc: ModuleNode[], node) => {
      const matchesSearch = node.title.toLowerCase().includes(term.toLowerCase());
      const filteredChildren = node.children ? filterModules(node.children, term) : [];
      
      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children
        });
      }
      
      return acc;
    }, []);
  };

  const renderModuleNode = (node: ModuleNode, level: number = 0): JSX.Element => {
    const isSelected = selectedModules.includes(node.key);
    const isExpanded = expandedNodes.includes(node.key);
    const hasChildren = node.children && node.children.length > 0;
    const isDisabled = disabled || isConsultMode;
    
    const filteredModules = filterModules(modules, searchTerm);
    const isVisible = searchTerm === '' || 
      node.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (hasChildren && node.children?.some(child => 
        child.title.toLowerCase().includes(searchTerm.toLowerCase())
      ));

    if (!isVisible && searchTerm !== '') return <React.Fragment key={node.key} />;

    return (
      <Box key={node.key}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            pl: level * 2 + 1,
            bgcolor: isSelected ? 'action.hover' : 'transparent',
            borderRadius: 1,
            '&:hover': {
              bgcolor: isDisabled ? 'transparent' : 'action.hover'
            }
          }}
        >
          {hasChildren && (
            <Button
              size="small"
              onClick={() => toggleNodeExpansion(node.key)}
              disabled={isDisabled}
              sx={{ minWidth: 24, p: 0 }}
            >
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </Button>
          )}
          
          <Checkbox
            checked={isSelected}
            disabled={isDisabled}
            onChange={(e) => {
              if (hasChildren) {
                handleParentToggle(node, e.target.checked);
              } else {
                handleModuleToggle(node.key, e.target.checked);
              }
            }}
            sx={{ mr: 1 }}
          />
          
          <Typography
            variant="body2"
            sx={{
              flexGrow: 1,
              fontWeight: hasChildren ? 600 : 400,
              color: isDisabled ? 'text.disabled' : 'text.primary'
            }}
          >
            {node.title}
          </Typography>
          
          {isSelected && (
            <Chip
              label="Sélectionné"
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        {hasChildren && isExpanded && (
          <Box>
            {node.children?.map(child => renderModuleNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  const filteredModules = filterModules(modules, searchTerm);
  const selectedCount = selectedModules.length;
  const totalCount = modules.length;

  return (
    <Paper
      elevation={2}
      sx={{
        height: 400,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          {title}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Input
            placeholder="Rechercher un module..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled || isConsultMode}
            startAdornment={<Search sx={{ mr: 1, color: 'inherit' }} />}
            sx={{
              flexGrow: 1,
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              color: 'inherit',
              '& .MuiInputBase-input': {
                color: 'inherit',
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)'
                }
              }
            }}
          />
          <Button
            size="small"
            variant="outlined"
            color='inherit'
            onClick={() => setOpenModal(true)}
            disabled={disabled}
            sx={{ ml: 1 }}
          >
            Ouvrir
          </Button>
        </Box>
        
        <Typography variant="caption" sx={{ mt: 1 }}>
          {selectedCount} / {totalCount} module(s) sélectionné(s)
        </Typography>
      </Box>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="lg">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: '70vh', overflow: 'auto', p: 1 }}>
            {!loading && !error && filteredModules.length === 0 && (
              <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                {searchTerm ? 'Aucun module trouvé pour cette recherche' : 'Aucun module disponible'}
              </Typography>
            )}
            {!loading && !error && filteredModules.map(node => renderModuleNode(node))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2
        }}
      >
        {loading && (
          <Typography sx={{ textAlign: 'center', py: 4 }}>
            Chargement des modules...
          </Typography>
        )}
        
        {error && (
          <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
            {error}
          </Typography>
        )}
        
        {!loading && !error && filteredModules.length === 0 && (
          <Typography sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
            {searchTerm ? 'Aucun module trouvé pour cette recherche' : 'Aucun module disponible'}
          </Typography>
        )}
        
        {!loading && !error && filteredModules.map(node => renderModuleNode(node))}
      </Box>

      {/* Footer */}
      {isConsultMode && (
        <Box
          sx={{
            p: 2,
            bgcolor: 'warning.light',
            borderTop: 1,
            borderColor: 'warning.main'
          }}
        >
          <Typography variant="caption" color="warning.dark">
            🔒 Mode consultation - Les modules ne peuvent pas être modifiés
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default ModuleMappingBox;
